import dayjs from 'dayjs';
import React, { useEffect } from 'react';
import {
  HttpError,
  IResourceComponentsProps,
  useOne,
  useTranslate,
  useNavigation,
} from '@pankod/refine-core';
import {
  Box,
  FormControl,
  FormHelperText,
  Grid,
  Stack,
  TextField,
  Typography,
  Backdrop,
  CircularProgress,
  Edit,
  Breadcrumbs,
  Link,
} from '@pankod/refine-mui';
import { useStepsForm } from '@pankod/refine-react-hook-form';
import { IGetDeviceUsings } from 'interfaces';
import _ from 'lodash';

export const DeviceUsingEdit: React.FC<
  IResourceComponentsProps
> = () => {
  const t = useTranslate();
  const { push } = useNavigation();

  const {
    refineCore: { onFinish, formLoading, id },
    register,
    setValue,
    getValues,
    formState: { errors },
  } = useStepsForm<IGetDeviceUsings, HttpError, IGetDeviceUsings>({
    stepsProps: {
      isBackValidate: false,
    },
    warnWhenUnsavedChanges: true,
    refineCoreProps: {
      redirect: false,
      onMutationSuccess: () => {
        push(`/device_usings?code=${currentDeviceUsings.data?.data?.code || ''}`);
      },
    }
  });
  const currentDeviceUsings = useOne<IGetDeviceUsings>({ id: id || '', resource: 'device_usings' });

  const expiryDate = currentDeviceUsings.data?.data?.expiry_date?.toDate();

  const onSubmit = async () => {
    const now = new Date();
    let expiryDate = getValues('expiry_date') as any;
    if (
      expiryDate
      && !(expiryDate instanceof Date)
      && typeof expiryDate === 'object'
      && 'seconds' in expiryDate
    ) {
      expiryDate = new Date(expiryDate.seconds * 1000);
    } else {
      expiryDate = new Date(expiryDate);
    }

    const data = {
      ...getValues(),
      expiry_date: expiryDate,
      updated_at: now,
    };

    await onFinish(data);
  };

  const renderForm = () => {
    return (
      <>
        <Grid
          container
          sx={{
            marginX: { xs: '0px' },
          }}
        >
          <Grid item xs={12}>
            <Grid container>
              <Grid item paddingX={4} xs={12}>
                <Stack gap="24px">
                  <FormControl>
                    <TextField
                      {...register('expiry_date', {
                        required: t('errors.required.field', {
                          field: 'expiry_date',
                        }),
                      })}
                      size="medium"
                      variant="filled"
                      required
                      label="Expiry Date (required)"
                      type="datetime-local"
                      defaultValue={expiryDate}
                    />
                    {errors.expiry_date && (
                      <FormHelperText error>
                        {errors.expiry_date.message}
                      </FormHelperText>
                    )}
                  </FormControl>
                </Stack>
              </Grid>
            </Grid>
          </Grid>
        </Grid>
      </>
    );
  };

  useEffect(() => {
    setValue('expiry_date', (expiryDate ? dayjs(expiryDate).format('YYYY-MM-DDTHH:mm:ss') : expiryDate) as any);
  }, [expiryDate]);

  return (
    <Edit
      title={<Typography variant="h5">Edit Offer Code</Typography>}
      isLoading={formLoading}
      saveButtonProps={{ onClick: onSubmit }}
      breadcrumb={
        <Breadcrumbs sx={{ paddingLeft: '16px', paddingTop: '16px' }}>
          <Link underline="hover" color="inherit" href="/Offers">
            Offers
          </Link>

          <Link
            underline="hover"
            color="inherit"
            href={`/device_usings?code=${currentDeviceUsings.data?.data?.code || ''}`}
          >
            Device Usings List
          </Link>

          <Typography sx={{ color: 'text.primary' }}>Edit</Typography>
        </Breadcrumbs>
      }
    >
      <Box
        component="form"
        sx={{
          display: 'flex',
          flexDirection: 'column',
        }}
        autoComplete="off"
      >
        <br />
        {renderForm()}
        <Backdrop
          sx={{
            color: '#fff',
            zIndex: (theme) => theme.zIndex.drawer + 1,
          }}
          open={formLoading}
        >
          <CircularProgress color="inherit" />
        </Backdrop>
      </Box>
    </Edit>
  );
};
